// @ts-check
/**
 * Context provider for projects list and related data.
 *
 * Manages state for projects with tags, organisation tags, loading and error states, grouping and filtering of project list.
 *
 * Centralising this logic allows for consistent state management and data access across components that need to display or interact with the projects list,
 * such as the navigation menu and project overview pages.
 *
 */
import React, { createContext, useContext, useMemo, useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

import useMenuBootstrap from '@datahooks/useMenuBootstrap';

import { getFilterLabels, filterFlatProjectList, buildGroupedProjects, filterGroupedProjects, STORAGE_KEY } from '@components/navigation/menuUtils';
import { saveToLocalStorage, loadFromLocalStorage, removeFromLocalStorage } from '@datahooks/metadata/DataUtils';

/**
 * Enough to identify a project and name it on screen. Use this wherever that is all a caller needs -
 * it asks for no more than it uses, so a caller can supply a project from any source.
 *
 * @typedef {{ projectId: number, title: string }} BasicProject
 */

/**
 * metriQ's own title for a project. The trusted source wherever a name is shown beside data that came
 * from somewhere else - an AI report, say - so the label never depends on what that source called it.
 *
 * @param {BasicProject[]} [projects]
 * @param {number|null} [projectId]
 * @param {string} [fallback] - Used when the project is not in the list.
 * @returns {string}
 */
export const projectTitle = (projects, projectId, fallback = '') =>
  projects?.find((project) => project.projectId === projectId)?.title || fallback;

/** Tag values applied to a project, keyed by tag slug. From buildProjectTagIndex. */
/** @typedef {Record<string, Array<{ id: number, title: string }>>} ProjectTags */

/**
 * What applyProjectTags produces: a project from user_projects with its tag data attached. The
 * Record keeps the rest of the API's project open, because nothing has typed one yet and a guess here
 * would be enforced on every consumer.
 *
 * @typedef {BasicProject & { project_tags: ProjectTags } & Record<string, any>} TaggedProject
 */

/**
 * What the provider supplies. Fields are `any` where the underlying data is still untyped - the point of
 * this typedef is not to describe them but to stop the hook narrowing to `never`, which it does because
 * the context defaults to null. Without it every consumer has to cast before it can read anything.
 *
 * @typedef {Object} ProjectsListContextValue
 * @property {TaggedProject[]} [projectsWithTags]
 * @property {boolean} isLoading
 * @property {() => Promise<void>} refetchAll
 * @property {any} orgTags
 * @property {any} error
 * @property {any} menuGroupTag
 * @property {any} openKey
 * @property {any} filters
 * @property {any} groupableTags
 * @property {any} filterableTags
 * @property {any} selectedGroupTag
 * @property {any} filterLabels
 * @property {any} filteredFlatProjects
 * @property {any} filteredProjectsByGroup
 * @property {any} doGroupTagChange
 * @property {any} handleFiltered
 * @property {any} removeFilter
 * @property {any} handleOpenKeyChange
 */

// The cast goes on the argument, not the variable: Context<T> is invariant through Provider, so
// annotating the variable cannot widen Context<null>.
const ProjectsListContext = createContext(/** @type {ProjectsListContextValue|null} */ (null));

const GROUP_KEY_STORAGE = `${STORAGE_KEY}_GROUPTAG`;
const FILTERS_STORAGE = `${STORAGE_KEY}_FILTERS`;
const OPENKEY_STORAGE = `${STORAGE_KEY}_OPENKEY`;

export const ProjectsListProvider = ({ children }) => {
  const { projectsWithTags, orgTags, isLoading, error, refetchAll } = useMenuBootstrap();

  const [menuGroupTag, setMenuGroupTag] = useState('None');
  const [openKey, setOpenKey] = useState(null);
  // Annotated because useState([]) infers never[], which rejects every later setFilters call. A filter is
  // { tag, value }; left as any[] because nothing has typed one yet.
  const [filters, setFilters] = useState(/** @type {any[]} */ ([]));

  const initialGroupLoad = useRef(false);
  const initialFilterLoad = useRef(false);

  const groupableTags = useMemo(() => orgTags.filter((tag) => tag.is_groupable), [orgTags]);
  const filterableTags = useMemo(() => orgTags.filter((tag) => tag.is_filter), [orgTags]);

  useEffect(() => {

    if (!initialGroupLoad.current && groupableTags.length > 0) {
      initialGroupLoad.current = true;

      const savedGroupTag = loadFromLocalStorage(GROUP_KEY_STORAGE, undefined);

      if (savedGroupTag && (savedGroupTag === 'None' || groupableTags.some((t) => t.tag_slug === savedGroupTag))) {
        setMenuGroupTag(savedGroupTag);
      }

      const savedOpenKey = loadFromLocalStorage(OPENKEY_STORAGE, undefined);

      if (savedOpenKey) {
        setOpenKey(savedOpenKey);
      }

    }
  }, [groupableTags]);

  useEffect(() => {

    if (!initialFilterLoad.current && filterableTags.length > 0) {
      initialFilterLoad.current = true;

      const savedFilters = loadFromLocalStorage(FILTERS_STORAGE, undefined);

      if (savedFilters && Array.isArray(savedFilters)) {
        const validFilters = savedFilters.filter((f) => {
          const tag = filterableTags.find((t) => t.tag_id === f.tag);

          if (!tag) {
            return false;
          }

          const value = tag.tag_values.find((v) => v.tag_value_id === f.value);
          return !!value;
        });

        if (validFilters.length > 0) {
          setFilters(validFilters);
        }
      }
    }
  }, [filterableTags]);

  const doGroupTagChange = useCallback((newTag) => {
    setMenuGroupTag(newTag);
    saveToLocalStorage(GROUP_KEY_STORAGE, newTag);
    setOpenKey(null);
    removeFromLocalStorage(OPENKEY_STORAGE);
  }, []);

  const handleFiltered = useCallback((filter) => {
    setFilters((prev) => {
      const exists = prev.find((f) => f.tag === filter.tag && f.value === filter.value);

      if (exists) {
        return prev;
      }

      const next = [...prev, filter];
      saveToLocalStorage(FILTERS_STORAGE, next);
      return next;
    });
  }, []);

  const removeFilter = useCallback((filter) => {
    setFilters((prev) => {
      const next = prev.filter((f) => !(f.tag === filter.tag && f.value === filter.value));
      saveToLocalStorage(FILTERS_STORAGE, next);
      return next;
    });
  }, []);

  const handleOpenKeyChange = useCallback((key) => {
    setOpenKey((current) => {
      const next = current === key ? null : key;
      saveToLocalStorage(OPENKEY_STORAGE, next);
      return next;
    });
  }, []);

  const selectedGroupTag = useMemo(() => {

    if (menuGroupTag === 'None') {
      return null;
    }

    return orgTags.find((tag) => tag.tag_slug === menuGroupTag) ?? null;
  }, [menuGroupTag, orgTags]);

  const filterLabels = useMemo(() => getFilterLabels(filters, filterableTags), [filters, filterableTags]);

  const filteredFlatProjects = useMemo(
    () => filterFlatProjectList(projectsWithTags, filters, filterableTags),
    [projectsWithTags, filters, filterableTags]
  );

  const projectsByGroup = useMemo(
    () => buildGroupedProjects(projectsWithTags, selectedGroupTag, groupableTags),
    [projectsWithTags, selectedGroupTag, groupableTags]
  );

  const filteredProjectsByGroup = useMemo(
    () => filterGroupedProjects(projectsByGroup, filters, filterableTags),
    [projectsByGroup, filters, filterableTags]
  );

  const value = useMemo(
    () => ({
      projectsWithTags,
      orgTags,
      isLoading,
      error,
      refetchAll,
      menuGroupTag,
      openKey,
      filters,
      groupableTags,
      filterableTags,
      selectedGroupTag,
      filterLabels,
      filteredFlatProjects,
      filteredProjectsByGroup,
      doGroupTagChange,
      handleFiltered,
      removeFilter,
      handleOpenKeyChange,
    }),
    [
      projectsWithTags,
      orgTags,
      isLoading,
      error,
      refetchAll,
      menuGroupTag,
      openKey,
      filters,
      groupableTags,
      filterableTags,
      selectedGroupTag,
      filterLabels,
      filteredFlatProjects,
      filteredProjectsByGroup,
      doGroupTagChange,
      handleFiltered,
      removeFilter,
      handleOpenKeyChange,
    ]
  );

  return <ProjectsListContext.Provider value={value}>{children}</ProjectsListContext.Provider>;
};

ProjectsListProvider.propTypes = {
  children: PropTypes.node,
};

export const useProjectsListContext = () => {
  const context = useContext(ProjectsListContext);

  if (!context) {
    throw new Error('useProjectsListContext must be used inside ProjectsListProvider');
  }

  return context;
};